import * as actionType from "./actionTypes";
import { getRequestAuthed, postRequestAuthed, processError, downloadFileAuthed } from "../../utils/apiHandlers";
import { LOADING_STATE, LS_ITEM_ADMIN, NOTIF_TYPE } from "../../utils/stateTypes";
import _notif from "../notification";
import { adminSelectors } from "../rootSelectors";

const setBranch = (key, value) => (
  {
    type: actionType.SET_BRANCH,
    key,
    value,
  }
);

export const setFilters = (key, filters) => (
  {
    type: actionType.SET_FILTERS,
    key,
    filters,
  }
);

const query = (params) => (
  Object.entries(params)
    .filter(([, v]) => (v !== null) && (v !== undefined) && (v !== ""))
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&")
);

//Loads a GET endpoint into a branch of the admin state
const load = (url, key, lsItem, transform=(x) => x) => (
  (dispatch) => {
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    getRequestAuthed(url,
      (result) => {
        dispatch(setBranch(key, transform(result)));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

//Posts an admin change, then notifies & runs the callback
const save = (url, data, lsItem, successText, successCB=null) => (
  (dispatch) => {
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed(url,
      data,
      (result) => {
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        if (successText) {
          dispatch(_notif.actions.queueNotification(successText, NOTIF_TYPE.SUCCESS));
        }
        if (successCB) {
          successCB(result);
        }
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const exportCsv = (url, fallbackName) => (
  (dispatch) => {
    const lsItem = LS_ITEM_ADMIN.EXPORT;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    downloadFileAuthed(url, fallbackName)
      .then(() => dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED)))
      .catch(processError(dispatch, _notif, lsItem));
  }
);

/* =========================== DASHBOARD & REPORTS =========================== */

export const loadSummary = () => load("/AdminDashboard/Summary", "summary", LS_ITEM_ADMIN.SUMMARY_LOAD);

export const loadReports = (days) => (
  (dispatch) => {
    dispatch(setBranch("reportDays", days));
    dispatch(load(`/AdminDashboard/Reports?days=${days}`, "reports", LS_ITEM_ADMIN.REPORTS_LOAD));
  }
);

/* =========================== MEMBERS =========================== */

export const loadMembers = (page=1) => (
  (dispatch, getState) => {
    const current = adminSelectors.getMembers(getState());
    dispatch(load(`/AdminMember/Members?${query({ ...current.filters, page })}`, "members", LS_ITEM_ADMIN.MEMBERS_LOAD,
      (r) => ({ ...current, items: r.members, total: r.total, page: r.page, pageSize: r.pageSize })));
  }
);

export const loadMember = (userID) => (
  (dispatch) => {
    dispatch(setBranch("member", null));
    dispatch(load(`/AdminMember/Member/${encodeURIComponent(userID)}`, "member", LS_ITEM_ADMIN.MEMBER_LOAD));
  }
);

//Reloads the open member without clearing it first (after an action)
const refreshMember = (userID) => load(`/AdminMember/Member/${encodeURIComponent(userID)}`, "member", LS_ITEM_ADMIN.MEMBER_LOAD);

export const grantAccess = (userId, planId, accessEndDate, notes, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminMember/GrantAccess", { userId, planId, accessEndDate: accessEndDate || null, notes }, LS_ITEM_ADMIN.MEMBER_ACTION, "Access granted", () => {
      dispatch(refreshMember(userId));
      successCB();
    }));
  }
);

export const adminCancelSubscription = (userId, subscriptionId, endAccessNow, reason, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminMember/CancelSubscription", { subscriptionId, endAccessNow, reason }, LS_ITEM_ADMIN.MEMBER_ACTION, "Subscription cancelled", () => {
      dispatch(refreshMember(userId));
      successCB();
    }));
  }
);

export const saveSubscriptionNotes = (userId, subscriptionId, adminNotes) => (
  (dispatch) => {
    dispatch(save("/AdminMember/UpdateNotes", { subscriptionId, adminNotes }, LS_ITEM_ADMIN.MEMBER_ACTION, "Notes saved", () => {
      dispatch(refreshMember(userId));
    }));
  }
);

export const sendPasswordReset = (userId) => save("/AdminMember/SendPasswordReset", { userId }, LS_ITEM_ADMIN.MEMBER_ACTION, "Password reset email sent");

/* =========================== SUBSCRIPTIONS =========================== */

export const loadSubscriptions = (page=1) => (
  (dispatch, getState) => {
    const current = adminSelectors.getSubscriptions(getState());
    dispatch(load(`/AdminMember/Subscriptions?${query({ ...current.filters, page })}`, "subscriptions", LS_ITEM_ADMIN.SUBSCRIPTIONS_LOAD,
      (r) => ({ ...current, items: r.subscriptions, total: r.total, page: r.page, pageSize: r.pageSize })));
  }
);

/* =========================== PAYMENTS =========================== */

export const loadPayments = (page=1) => (
  (dispatch, getState) => {
    const current = adminSelectors.getPayments(getState());
    dispatch(load(`/AdminPayment/Payments?${query({ ...current.filters, page })}`, "payments", LS_ITEM_ADMIN.PAYMENTS_LOAD,
      (r) => ({ ...current, items: r.payments, total: r.total, page: r.page, pageSize: r.pageSize, totals: r.totals })));
  }
);

export const loadNewsletter = () => load("/AdminPayment/Newsletter", "newsletter", LS_ITEM_ADMIN.NEWSLETTER_LOAD);
export const loadPayFastLog = () => load("/AdminPayment/PayFastLog", "payFastLog", LS_ITEM_ADMIN.NEWSLETTER_LOAD);

/* =========================== CLASSES, LIBRARIES & PLANS =========================== */

export const loadClasses = () => load("/AdminContent/Classes", "classes", LS_ITEM_ADMIN.CLASSES_LOAD);
export const loadCollections = () => load("/AdminContent/Collections", "collections", LS_ITEM_ADMIN.CLASSES_LOAD);
export const loadPlans = () => load("/AdminContent/Plans", "plans", LS_ITEM_ADMIN.PLANS_LOAD);

export const saveClass = (item, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminContent/SaveClass", item, LS_ITEM_ADMIN.CLASS_SAVE, "Class saved", (result) => {
      dispatch(loadClasses());
      successCB(result);
    }));
  }
);

export const deleteClass = (id, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminContent/DeleteClass", { id }, LS_ITEM_ADMIN.CLASS_SAVE, "Class deleted", () => {
      dispatch(loadClasses());
      successCB();
    }));
  }
);

export const saveCollection = (item, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminContent/SaveCollection", item, LS_ITEM_ADMIN.PLAN_SAVE, "Library saved", () => {
      dispatch(loadCollections());
      successCB();
    }));
  }
);

export const loadSeries = () => load("/AdminContent/Series", "series", LS_ITEM_ADMIN.CLASSES_LOAD);

export const saveSeries = (item, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminContent/SaveSeries", item, LS_ITEM_ADMIN.PLAN_SAVE, "Tag saved", () => {
      dispatch(loadSeries());
      successCB();
    }));
  }
);

export const deleteSeries = (id) => (
  (dispatch) => {
    dispatch(save("/AdminContent/DeleteSeries", { id }, LS_ITEM_ADMIN.PLAN_SAVE, "Tag deleted", () => {
      dispatch(loadSeries());
      dispatch(loadClasses());
    }));
  }
);

/* =========================== WEEKLY PLANS =========================== */

export const loadWeeklyPlans = () => load("/AdminContent/WeeklyPlans", "weeklyPlans", LS_ITEM_ADMIN.WEEKLY_PLANS_LOAD);

/* =========================== COMMENTS =========================== */

export const loadComments = (page=1) => (
  (dispatch, getState) => {
    const current = adminSelectors.getComments(getState());
    dispatch(load(`/AdminComment/Comments?${query({ ...current.filters, page })}`, "comments", LS_ITEM_ADMIN.COMMENTS_LOAD,
      (r) => ({ ...current, items: r.comments, total: r.total, page: r.page, pageSize: r.pageSize, unanswered: r.unanswered })));
  }
);

export const replyToComment = (parentId, text, successCB) => (
  (dispatch, getState) => {
    dispatch(save("/AdminComment/Reply", { parentId, text }, LS_ITEM_ADMIN.COMMENT_SAVE, "Reply posted", () => {
      dispatch(loadComments(adminSelectors.getComments(getState()).page));
      successCB();
    }));
  }
);

export const deleteComment = (id) => (
  (dispatch, getState) => {
    dispatch(save("/AdminComment/Delete", { id }, LS_ITEM_ADMIN.COMMENT_SAVE, "Comment removed", () => {
      dispatch(loadComments(adminSelectors.getComments(getState()).page));
    }));
  }
);

export const savePlan = (item, successCB) => (
  (dispatch) => {
    dispatch(save("/AdminContent/SavePlan", item, LS_ITEM_ADMIN.PLAN_SAVE, "Plan saved", () => {
      dispatch(loadPlans());
      successCB();
    }));
  }
);

//Used to build the CSV export links with the current filters
export { query as buildQuery };
